import {ReactElement, ReactNode, useEffect} from 'react';
import clsx from 'clsx';
import {useAuth} from '@common/auth/use-auth';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {Menu, MenuTrigger} from '@common/ui/navigation/menu/menu-trigger';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {createSvgIconFromTree} from '@common/icons/create-svg-icon';
import {Trans} from '@common/i18n/trans';
import {IconButton} from '@common/ui/buttons/icon-button';
import {Item} from '@common/ui/forms/listbox/item';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {CustomMenu} from '@common/menus/custom-menu';
import {useSettings} from '@common/core/settings/use-settings';
import {ButtonColor} from '@common/ui/buttons/get-shared-button-style';
import {MenuIcon} from '@common/icons/material/Menu';
import {MenuItemConfig} from '@common/core/settings/settings';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import {NavbarAuthButtons} from '@common/ui/navigation/navbar/navbar-auth-buttons';
import {useDarkThemeVariables} from '@common/ui/themes/use-dark-theme-variables';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {useLightThemeVariables} from '@common/ui/themes/use-light-theme-variables';

type NavbarColor = 'primary' | 'bg' | 'bg-alt' | 'transparent' | string;

export interface NavbarProps {
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md';
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
}
export function Navbar(props: NavbarProps) {
  let {
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color,
    textColor,
    darkModeColor,
    rightChildren,
    menuPosition,
    logoColor,
    primaryButtonColor,
    authMenuItems,
    alwaysDarkMode = false,
    wrapInContainer = false,
  } = props;
  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  const {notifications} = useSettings();
  const {isLoggedIn} = useAuth();
  const darkThemeVars = useDarkThemeVariables();
  const lightThemeVars = useLightThemeVariables();
  const showNotifButton = isLoggedIn && notifications?.integrated;
  color = color ?? lightThemeVars?.['--be-navbar-color'] ?? 'primary';
  darkModeColor =
    darkModeColor ?? darkModeColor?.['--be-navbar-color'] ?? 'bg-alt';

  if (isDarkMode) {
    color = darkModeColor;
  }

  const isLandingNavbar = menuPosition === 'homepage-navbar';

  if (isLandingNavbar) {
    return (
      <header className="header-fp p-0 w-100 fixed-top">
        <nav className="navbar navbar-expand-lg py-10 landing-navbar">
          <div className="container-fluid landing-navbar-container">
            {!hideLogo && (
              <a href="/" className="text-nowrap logo-img landing-logo-link">
                <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
              </a>
            )}

            {/* Mobile menu toggle */}
            <button
              className="navbar-toggler border-0 p-0 shadow-none landing-mobile-toggle"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasRight"
              aria-controls="offcanvasRight">
              <i className="material-icons landing-menu-icon">menu</i>
            </button>

            {/* Desktop navigation menu */}
            <div className="align-items-center landing-nav-menu">
              <ul className="navbar-nav landing-nav-list">
                <li className="nav-item">
                  <a className="nav-link landing-nav-link" href="#home">
                    <Trans message="Home" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link landing-nav-link" href="#features">
                    <Trans message="Features" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link landing-nav-link landing-nav-link-with-badge" href="#solutions">
                    <Trans message="Solutions" />
                    <span className="badge landing-new-badge">
                      <Trans message="New" />
                    </span>
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link landing-nav-link" href="#reviews">
                    <Trans message="Reviews" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link landing-nav-link" href="#pricing">
                    <Trans message="Pricing" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link landing-nav-link" href="/contact">
                    <Trans message="Contact Us" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Desktop authentication buttons */}
            <div className="landing-auth-buttons">
              {isLoggedIn ? (
                <a href="/dashboard" className="btn btn-primary btn-sm landing-login-btn">
                  <Trans message="Dashboard" />
                </a>
              ) : (
                <div className="landing-button-group">
                  <a href="/login" className="btn btn-primary btn-sm landing-login-btn">
                    <Trans message="Log In" />
                  </a>
                  <a href="/register" className="btn btn-primary-outline btn-sm landing-register-btn">
                    <Trans message="Get Started" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </nav>
        
        {/* Mobile navigation menu */}
        <div className="offcanvas offcanvas-end landing-offcanvas" tabIndex={-1} id="offcanvasRight" aria-labelledby="offcanvasRightLabel">
          <div className="offcanvas-header">
            {!hideLogo && (
              <a href="/" className="text-nowrap logo-img">
                <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
              </a>
            )}
            <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
          </div>
          <div className="offcanvas-body">
            <ul className="list-unstyled">
              <li className="mb-1">
                <a href="#home" className="landing-nav-link">
                  <Trans message="Home" />
                </a>
              </li>
              <li className="mb-1">
                <a href="#features" className="landing-nav-link">
                  <Trans message="Features" />
                </a>
              </li>
              <li className="mb-1">
                <a href="#solutions" className="landing-nav-link landing-nav-link-with-badge">
                  <Trans message="Solutions" />
                  <span className="landing-new-badge">
                    <Trans message="New" />
                  </span>
                </a>
              </li>
              <li className="mb-1">
                <a href="#reviews" className="landing-nav-link">
                  <Trans message="Reviews" />
                </a>
              </li>
              <li className="mb-1">
                <a href="#pricing" className="landing-nav-link">
                  <Trans message="Pricing" />
                </a>
              </li>
              <li className="mb-1">
                <a href="/contact" className="landing-nav-link">
                  <Trans message="Contact Us" />
                </a>
              </li>
              <li className="mobile-auth-section">
                {isLoggedIn ? (
                  <a href="/dashboard" className="mobile-login-btn">
                    <Trans message="Dashboard" />
                  </a>
                ) : (
                  <div>
                    <a href="/login" className="mobile-login-btn">
                      <Trans message="Log In" />
                    </a>
                    <a href="/register" className="mobile-register-btn">
                      <Trans message="Get Started" />
                    </a>
                  </div>
                )}
              </li>
            </ul>
          </div>
        </div>
      </header>
    );
  }

  return (
    <div
      style={alwaysDarkMode ? darkThemeVars : undefined}
      className={clsx(
        getColorStyle(color, textColor),
        size === 'md' && 'h-64 py-8',
        size === 'sm' && 'h-54 py-4',
        size === 'xs' && 'h-48 py-4',
        border,
        className,
      )}
    >
      <div
        className={clsx(
          'flex h-full items-center justify-between gap-4 px-4 md:px-8 lg:px-20',
          wrapInContainer && 'container mx-auto',
        )}
      >
        {/* Left section: Logo and toggle button */}
        <div className="flex items-center gap-4">
          {!hideLogo && (
            <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
          )}
          {toggleButton}
        </div>

        {/* Center section: Navigation menu */}
        <div className="hidden lg:flex items-center justify-center flex-1">
          {children}
          <DesktopMenu position={menuPosition} />
        </div>

        {/* Right section: Auth and notifications */}
        <div className="flex items-center gap-2 md:gap-4 lg:gap-6">
          {rightChildren}
          {showNotifButton && <NotificationDialogTrigger />}
          {isLoggedIn ? (
            <NavbarAuthUser items={authMenuItems} />
          ) : (
            <NavbarAuthButtons
              navbarColor={color}
              primaryButtonColor={primaryButtonColor}
            />
          )}
        </div>

        {/* Mobile menu trigger */}
        <div className="lg:hidden">
          <MobileMenu position={menuPosition} />
        </div>
      </div>
    </div>
  );
}

interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}

function DesktopMenu({position}: DesktopMenuProps) {
  const menu = useCustomMenu(position);
  
  const HomepageMenu: MenuItemConfig[] = [
    { id: 'home', label: 'Home', action: '#home', type: 'link', order: 1 },
    { id: 'features', label: 'Features', action: '#features', type: 'link', order: 2 },
    { id: 'solutions', label: 'Solutions', action: '#solutions', type: 'link', order: 3 },
    { id: 'reviews', label: 'Reviews', action: '#reviews', type: 'link', order: 4 },
    { id: 'pricing', label: 'Pricing', action: '#pricing', type: 'link', order: 5 },
    { id: 'contact', label: 'Contact Us', action: '#contact-us', type: 'link', order: 6 },
  ];
  
  let menuItems: MenuItemConfig[];
  if (position === 'homepage-navbar') {
    menuItems = HomepageMenu;
  } else if (!menu?.items.length) {
    // Fallback menu items for other positions
    menuItems = [];
  } else {
    menuItems = menu.items;
  }

  return (
    <ul 
      className="flex items-center gap-6 lg:gap-8 xl:gap-10"
      style={{listStyle: 'none', margin: 0, padding: 0}}
    >
      {menuItems.map((item, index) => {
        const Icon = ('icon' in item && item.icon) ? createSvgIconFromTree(item.icon) : undefined;
        const target = ('target' in item) ? item.target : undefined;
        return (
          <li key={item.id} className="nav-item">
            <a 
              className="nav-link text-sm md:text-base font-medium hover:text-primary transition-colors duration-200" 
              href={item.action}
              target={target}
            >
              {Icon && <Icon className="mr-2" />}
              <Trans message={item.label} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}

interface MobileMenuProps {
  position: NavbarProps['menuPosition'];
}
function MobileMenu({position}: MobileMenuProps) {
  const navigate = useNavigate();
  const menu = useCustomMenu(position);

  const HomepageMenu: MenuItemConfig[] = [
    { id: 'home', label: 'Home', action: '#home', type: 'link', order: 1 },
    { id: 'features', label: 'Features', action: '#features', type: 'link', order: 2 },
    { id: 'solutions', label: 'Solutions', action: '#solutions', type: 'link', order: 3 },
    { id: 'reviews', label: 'Reviews', action: '#reviews', type: 'link', order: 4 },
    { id: 'pricing', label: 'Pricing', action: '#pricing', type: 'link', order: 5 },
    { id: 'contact', label: 'Contact Us', action: '#contact-us', type: 'link', order: 6 },
  ];
  
  let menuItems: MenuItemConfig[];
  if (position === 'homepage-navbar') {
    menuItems = HomepageMenu;
  } else if (!menu?.items.length) {
    menuItems = [];
  } else {
    menuItems = menu.items;
  }

  const handleItemClick = (item: any) => {
    if (item.type === 'route') {
      navigate(item.action);
    } else {
      window.open(item.action, ('target' in item) ? item.target : '_self')?.focus();
    }
  };

  return (
    <MenuTrigger>
      <IconButton size="sm" className="lg:hidden" aria-label="Toggle menu">
        <MenuIcon />
      </IconButton>
      <Menu>
        {menuItems.map(item => {
          const Icon = ('icon' in item && item.icon) ? createSvgIconFromTree(item.icon) : undefined;
          return (
            <Item
              value={item.action}
              onSelected={() => handleItemClick(item)}
              key={item.id}
              startIcon={Icon && <Icon />}
              className="px-4 py-2"
            >
              <Trans message={item.label} />
            </Item>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}

function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} border-b-primary`;
    case 'bg':
      return `bg ${textColor || 'text-main'} border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}
